import CssBaseline from '@material-ui/core/CssBaseline';
import {
  jssPreset,
  StylesProvider,
  ThemeProvider,
} from '@material-ui/core/styles';
import {MuiPickersUtilsProvider} from '@material-ui/pickers';
import {BackButtonListener} from 'components/BackButton';
import {BottomSheetProvider} from 'components/BottomSheet';
import PreLoader from 'components/PreLoader';
import {googleAnalyticsTrackingCode} from 'configs/app';
import theme from 'configs/theme';
import {StoreProvider, useStoreState} from 'easy-peasy';
import 'fontsource-roboto';
import {create} from 'jss';
import rtl from 'jss-rtl';
import ModalProvider from 'mui-modal-provider';
import {SnackbarProvider} from 'notistack';
import React, {Suspense, useEffect, useState} from 'react';
import ReactGA from 'react-ga4';
import {ReactQueryConfigProvider} from 'react-query';
import {Provider as ReduxProvider} from 'react-redux';
import {Redirect, Route, Router, Switch, useLocation} from 'react-router-dom';
import store from 'store';
import {BottomSheetConfigurator} from 'utils/bottom-sheet';
import JalaaliUtcUtils from 'utils/jalali-utc';
import 'utils/moment';
import {OutHookConfigurator} from 'utils/out-hook';
import routerHistory from 'utils/router-history';
import {SnackbarUtilsConfigurator} from 'utils/snackbar';
import {init as initEventService} from 'services/Events';
import packageJson from '../package.json';
import {sleep} from './utils';
import NotFoundError from 'components/NotFoundPage';
import ServerDown from 'components/ServerDown';
import Hotjar from '@hotjar/browser';
import Clarity from '@microsoft/clarity';
import SplashScreen from 'views/app/dashboard/SplashScreen';

const ViewApp = React.lazy(() =>
  import(/* webpackChunkName: "app" */ 'views/app'),
);
const ViewPublic = React.lazy(() =>
  import(/* webpackChunkName: "app" */ 'views/public'),
);
const ViewUser = React.lazy(() =>
  import(/* webpackChunkName: "app" */ 'views/user'),
);
const ViewError = React.lazy(() =>
  import(/* webpackChunkName: "app" */ 'views/error'),
);
const AddToHomeScreen = React.lazy(() =>
  import(/* webpackChunkName: "app" */ 'views/add-to-home-screen'),
);
const AutoLogin = React.lazy(() =>
  import(/* webpackChunkName: "app" */ 'views/auto-login'),
);
const PartnerLogin = React.lazy(() =>
  import(/* webpackChunkName: "app" */ 'views/partner-login'),
);

const PotlifeLogin = React.lazy(() =>
  import(/* webpackChunkName: "app" */ 'views/potlife-login'),
);

const AuthRoute = ({component: Component, loggedIn, ...rest}) => {
  const regex = RegExp(
    '/app/advisers/[0-f-]{36}/(booking|booking-homecare|booking-family-doctor)',
  );
  const exclude = regex.test(rest.location.pathname);

  return (
    <Route
      {...rest}
      render={(props) =>
        exclude || loggedIn ? (
          <Component {...props} />
        ) : (
          <Redirect
            to={{
              pathname: '/user/login',
              state: {from: props.location},
            }}
          />
        )
      }
    />
  );
};

initEventService();
const jss = create({plugins: [...jssPreset().plugins, rtl()]});

function MaterialThemeProvider(props) {
  return (
    <ThemeProvider theme={theme}>
      <StylesProvider jss={jss}>
        <CssBaseline />
        {props.children}
      </StylesProvider>
    </ThemeProvider>
  );
}

const queryConfig = {
  queries: {
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: false,
  },
};

function Application() {
  const loggedIn = useStoreState((state) => state.auth.loggedIn);
  // const ignoreA2HS = useStoreState((state) => state.app.ignoreA2HS);

  return (
    <ReactQueryConfigProvider config={queryConfig}>
      <MaterialThemeProvider>
        <MuiPickersUtilsProvider utils={JalaaliUtcUtils} locale="fa">
          <Suspense fallback={<PreLoader />}>
            <Router history={routerHistory}>
              {/* {!loggedIn && !ignoreA2HS && !isIOS && <ForceAddToHomeScreen />} */}
              <ModalProvider>
                <BottomSheetProvider>
                  <SnackbarProvider
                    maxSnack={5}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center',
                    }}
                    autoHideDuration={3 * 1000}
                  >
                    <BottomSheetConfigurator />
                    <SnackbarUtilsConfigurator />
                    <OutHookConfigurator />
                    <BackButtonListener />
                    <Switch>
                      {/*<AuthRoute*/}
                      {/*  path="/app"*/}
                      {/*  loggedIn={loggedIn}*/}
                      {/*  component={ViewApp}*/}
                      {/*/>*/}
                      <Route
                        path="/app"
                        render={(props) => <ViewApp {...props} />}
                      />
                      <Route
                        path="/public"
                        render={(props) => <ViewPublic {...props} />}
                      />
                      <Route
                        path="/user"
                        render={(props) => <ViewUser {...props} />}
                      />
                      <Route
                        path="/error"
                        exact
                        render={(props) => <NotFoundError {...props} />}
                      />
                      <Route
                        path="/add-to-home-screen"
                        exact
                        render={(props) => <AddToHomeScreen {...props} />}
                      />

                      {/*<Route*/}
                      {/*  path="/auto-login/:refreshToken+"*/}
                      {/*  render={(props) => <AutoLogin {...props} />}*/}
                      {/*/>*/}

                      {/*<Route*/}
                      {/*  path="/partner-login"*/}
                      {/*  render={(props) => <PartnerLogin {...props} />}*/}
                      {/*/>*/}
                      <Route
                        path="/potlife-login/:data"
                        render={(props) => <PotlifeLogin {...props} />}
                      />

                      <Route
                        exact
                        path="/"
                        render={() => <Redirect to="/app" />}
                      />
                      <Route
                        path="/serverdown"
                        exact
                        render={(props) => <ServerDown {...props} />}
                      />
                    </Switch>
                  </SnackbarProvider>
                </BottomSheetProvider>
              </ModalProvider>
            </Router>
          </Suspense>
        </MuiPickersUtilsProvider>
      </MaterialThemeProvider>
    </ReactQueryConfigProvider>
  );
}

export default function App() {
  //hotjar
  const siteId = 5362543;
  const hotjarVersion = 6;
  const isAdviseSession = window.location.pathname.includes('advise-sessions');
  useEffect(() => {
    if (isAdviseSession) {
      if (window.hj) {
        window.hj('optOut');
        window.hj('pauseRecording');
      }
    } else {
      if (!window.hj) {
        Hotjar.init(siteId, hotjarVersion);
      } else {
        window.hj('optIn');
        window.hj('resumeRecording');
      }
    }
  }, [isAdviseSession]);

  //clarify
  useEffect(() => {
    const projectId = 'r2vp44t9sl';

    Clarity.init(projectId);
  }, []);

  useEffect(() => {
    ReactGA.initialize(googleAnalyticsTrackingCode);
    ReactGA.send({
      hitType: 'pageview',
      page: window.location.pathname + window.location.search,
    });
  }, []);
  /////////splash screen//////////////////////////////
  const isIOS = /iphone|ipad|ipod/.test(
    window.navigator.userAgent.toLowerCase(),
  );
  const isInStandaloneMode = () =>
    window.matchMedia('(display-mode: standalone)').matches;
  const [loading, setLoading] = useState(() => {
    const splashShown = sessionStorage.getItem('splash-shown');
    return isInStandaloneMode() && isIOS && !splashShown;
  });

  useEffect(() => {
    if (loading && isInStandaloneMode()) {
      const timer = setTimeout(() => {
        setLoading(false);
        sessionStorage.setItem('splash-shown', 'true');
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  if (loading && isInStandaloneMode()) {
    return <SplashScreen />;
  }
  ///////////////

  return (
    <ReduxProvider store={store}>
      <StoreProvider store={store}>
        <Application />
      </StoreProvider>
    </ReduxProvider>
  );
}

export async function checkVersion() {
  try {
    let version = localStorage.getItem('version');
    if (version !== packageJson.version) {
      localStorage.setItem('version', packageJson.version);

      if ('caches' in window) {
        caches.keys().then((names) => {
          names.forEach((name) => {
            caches.delete(name);
          });
        });

        await sleep(3 * 1000);
        return <Redirect to="/" />;
      }
    } else {
      return packageJson.version;
    }
  } catch (e) {
    console.log(e);
  }
}

export function checkBooking(history) {
  const to = sessionStorage.getItem('booking');
  if (!!to) {
    history.push(to);
    sessionStorage.removeItem('booking');
  }
}

export function checkPackage(history) {
  const to = sessionStorage.getItem('package');
  if (!!to) {
    history.push({
      pathname: to,
      state: {
        showModal: true,
      },
    });
    sessionStorage.removeItem('package');
  }
}

export function checkPsychologyInstanceCall(history) {
  const to = sessionStorage.getItem('psychologyInstanceCall');
  if (!!to) {
    history.push(to);
    sessionStorage.removeItem('psychologyInstanceCall');
  }
}

export function checkMedicineInstanceCall(history) {
  const to = sessionStorage.getItem('medicineInstanceCall');
  if (!!to) {
    history.push(to);
    sessionStorage.removeItem('medicineInstanceCall');
  }
}

export function checkBecomeAdviser(history) {
  const to = sessionStorage.getItem('becomeAdviser');
  if (!!to) {
    history.push(to);
    sessionStorage.removeItem('becomeAdviser');
  }
}
